/**
 * The mine's props at both tiers: timber frames across straight corridors
 * and along each room's rear wall (none in the boss's room, whose back-wall
 * frame is the boss marker's), the rails (`rails.ts`), the set dressing
 * (`dressing.ts`), and rock shoulders on walls beside open ground (lifted
 * from the dungeon-floors prototype's `dungeon-tile-kit.js`; the rock
 * shoulders draw at Off only and stay off the stubs the ledge dressing uses).
 * Placement is seeded by the floor's seed and the theme's `propRules`; it
 * never blocks a tile (props are drawn, not walked).
 */
import type { Floor, Unit } from '@ab/game-core';
import type { Theme } from '@ab/content-schema';
import { type Part, PartBatch, part } from './batch.ts';
import { presentation } from './constants.ts';
import { type DressingParts, buildDressing } from './dressing.ts';
import type { WallRecord } from './floor-geometry.ts';
import { isOpen, roomAt, tileIndex, tileRandom } from './grid.ts';
import type { Bounds, FrameSite, Track } from './lighting-plan.ts';
import type { Palette } from './palette.ts';
import { buildRails } from './rails.ts';

export type PropParts = {
  readonly timber: PartBatch;
  readonly iron: PartBatch;
  readonly rock: PartBatch;
  /** Every frame (corridor portal or room header), for the Medium tier's lanterns. */
  readonly frames: readonly FrameSite[];
  /** Rail tiles and the tiles each joins, for the fixtures' rail clearance. */
  readonly tracks: readonly Track[];
  /** Standing frame parts and dressing, which fixtures must not cut into. */
  readonly obstacles: readonly Bounds[];
  /** The dressing's shape batches: loose rock, barrel staves, iron bands, tufts. */
  readonly shaped: { readonly rock: PartBatch; readonly timberRound: PartBatch; readonly ironRound: PartBatch; readonly foliage: PartBatch };
  /** The track's ballast and grime (Medium only; the floor batch). */
  readonly bed: readonly Part[];
  readonly dressing: DressingParts['counts'];
};

/** Boxes in one frame: two posts, four bands, two braces, one beam. */
const framePartCount = 9;

/** A part's world-axis bounds, ignoring its rotation about y beyond the larger of its two ground sizes. */
const boundsOf = (p: Part): Bounds => {
  const half = Math.max(p.sx, p.sz) / 2;
  return { x0: p.x - half, x1: p.x + half, z0: p.z - half, z1: p.z + half, y0: p.y - p.sy / 2, y1: p.y + p.sy / 2 };
};

export const buildProps = (floor: Floor, seed: number, theme: Theme, palette: Palette, walls: readonly WallRecord[], units: readonly Unit[], topAt: (x: number, z: number) => number | null): PropParts => {
  const timber = new PartBatch('timber');
  const iron = new PartBatch('iron');
  const rock = new PartBatch('rock');
  const rules = theme.propRules;
  const k = presentation.props;
  const max = rules.maxDetailBoxes;
  let count = 0;
  const frames: FrameSite[] = [];
  const obstacles: Bounds[] = [];
  /** Every frame part's bounds, posts marked, for the dressing's reservations. */
  const obstacleParts: (Bounds & { post: boolean })[] = [];
  const add = (batch: PartBatch, ...args: Parameters<typeof part>): void => {
    batch.add(part(...args));
    count += 1;
  };
  /** A frame part: the cutaway may open it, it collapses with its header, and fixtures keep clear of it. */
  const framePart = (batch: PartBatch, link: readonly [number, number, number, number], ...args: Parameters<typeof part>): void => {
    const [tile, x, y, z, size, tint, extra] = args;
    add(batch, tile, x, y, z, size, tint, { ...extra, cutaway: 1, link });
    const half = Math.max(size[0], size[2]) / 2;
    const b = { x0: x - half, x1: x + half, z0: z - half, z1: z + half, y0: y - size[1] / 2, y1: y + size[1] / 2 };
    if (b.y1 > presentation.medium.fixtures.obstacleMinTop) obstacles.push(b);
    obstacleParts.push({ ...b, post: batch === timber && extra?.rz === undefined && size[1] > k.post });
  };

  /** A braced timber frame: two posts with iron bands, two braces and a beam. `axis` x spans east-west. */
  const frame = (x: number, y: number, axis: 'x' | 'z', length: number, kind: FrameSite['kind']): void => {
    if (count + framePartCount > max) return;
    const tile = tileIndex(floor, Math.round(x), Math.round(y));
    const height = rules.supportHeightPercent / 100 + (tileRandom(seed, Math.round(x), Math.round(y), 1) - 0.5) * k.heightJitter;
    const along = axis === 'x';
    const half = length / 2 - k.post / 2;
    const beam = length + k.beamOverhang;
    const link = [x, height + k.beamLift, -y, along ? beam / 2 : -beam / 2] as const;
    for (const side of [-1, 1]) {
      const px = x + (along ? side * half : 0);
      const pz = -y + (along ? 0 : side * half);
      framePart(timber, link, tile, px, height / 2, pz, [k.post, height, k.post], palette.timber);
      for (const by of [k.lowerBandY, height - k.upperBandDrop]) framePart(iron, link, tile, px, by, pz, [k.band, k.bandHeight, k.band], palette.iron);
      const bx = x + (along ? side * (half - k.post) : 0);
      const bz = -y + (along ? 0 : side * (half - k.post));
      framePart(timber, link, tile, bx, height - k.beamHeight, bz, [k.brace, k.braceLength, k.brace], palette.timber, { ry: along ? 0 : Math.PI / 2, rz: side * k.braceTilt });
    }
    framePart(timber, link, tile, x, height + k.beamLift, -y, along ? [beam, k.beamHeight, k.beamDepth] : [k.beamDepth, k.beamHeight, beam], palette.timber);
    frames.push({ kind, x, y, axis, height });
  };

  if (rules.roomCornerSupports) {
    // The boss's room has no header: its back-wall frame is the boss marker's (`markers.ts`).
    const bossRooms = new Set(units.filter((u) => u.boss !== null && !u.down).map((u) => roomAt(floor, u.pos.x, u.pos.y)));
    for (const room of floor.rooms.filter((_, i) => !bossRooms.has(i))) frame(room.x + (room.w - 1) / 2, room.y + room.h - 1 + k.roomFrameOffset, 'x', Math.max(1, room.w - k.roomFrameInset), 'room-frame');
  }

  // Rails (`rails.ts`), within a share of what the frames left of the detail budget.
  const rails = buildRails(floor, seed, theme, palette, Math.max(0, Math.floor((max - count) * presentation.rails.budgetShare)));
  for (const p of rails.iron) iron.add(p);
  for (const p of rails.timber) timber.add(p);
  count += rails.iron.length + rails.timber.length;

  // Frames across straight corridors, every `supportEvery` tiles with a seeded phase.
  const spacing = rules.supportEvery;
  for (let y = 1; y < floor.height - 1; y += 1) {
    for (let x = 1; x < floor.width - 1; x += 1) {
      if (count + framePartCount > max || !isOpen(floor, x, y) || roomAt(floor, x, y) >= 0 || floor.tiles[tileIndex(floor, x, y)] === 'exit') continue;
      const n = isOpen(floor, x, y + 1);
      const s = isOpen(floor, x, y - 1);
      const e = isOpen(floor, x + 1, y);
      const w = isOpen(floor, x - 1, y);
      const axis = n && s && !e && !w ? 'x' : e && w && !n && !s ? 'z' : null;
      if (axis === null) continue;
      const along = axis === 'x' ? y : x;
      const phase = Math.floor(tileRandom(seed, axis === 'x' ? x : 0, axis === 'z' ? y : 0, 44) * spacing);
      if ((along + phase) % spacing === 0) frame(x, y, axis, k.corridorFrameLength, 'portal');
    }
  }

  // Set dressing (`dressing.ts`) keeps clear of low frame parts and rail ties.
  const structures = [
    ...obstacleParts.filter((b) => b.y0 < k.lowStructure).map((b) => ({ ...b, post: b.post })),
    ...rails.timber.map((t) => ({ ...boundsOf(t), rail: true })),
  ];
  const dressing = buildDressing({ floor, units, seed, theme, palette, walls, structures, topAt });
  for (const p of dressing.parts.timber) timber.add(p);
  for (const p of dressing.parts.iron) iron.add(p);
  const shaped = { rock: new PartBatch('dressing-rock'), timberRound: new PartBatch('dressing-staves'), ironRound: new PartBatch('dressing-bands'), foliage: new PartBatch('dressing-tufts') };
  for (const p of dressing.parts.rock) shaped.rock.add(p);
  for (const p of dressing.parts.timberRound) shaped.timberRound.add(p);
  for (const p of dressing.parts.ironRound) shaped.ironRound.add(p);
  for (const p of dressing.parts.foliage) shaped.foliage.add(p);
  for (const b of dressing.bounds) if (b.y1 > presentation.medium.fixtures.obstacleMinTop) obstacles.push(b);

  // Rock shoulders embedded in walls beside open ground (never on a stub the ledge dressing uses).
  for (const wall of walls) {
    if (count >= max || wall.mask === 15 || dressing.ledgeWalls.has(wall.index)) continue;
    const d = tileRandom(seed, wall.x, wall.y, 5);
    if (d > (rules.rockDetailDensityPercent / 100) * k.rockDensityScale) continue;
    const wideX = (wall.mask & 2) !== 0;
    const wideZ = (wall.mask & 4) !== 0;
    const r = k.rock;
    const at = ([base, extra]: readonly [number, number], t: number): number => base + extra * t;
    const span = at(wideX || wideZ ? r.wideSpan : r.narrowSpan, d);
    const sx = wideX ? span : at(r.width, d);
    const sz = !wideX && wideZ ? span : at(r.depth, 1 - d);
    const sy = at(wall.cutaway ? r.stubHeight : r.tallHeight, d);
    const y = wall.height - (wall.cutaway ? r.stubSink : at(r.tallSink, d));
    add(rock, wall.index, wall.x + (wideX ? r.wideOffset : 0), y, -wall.y + (wideZ && !wideX ? r.wideOffset : 0), [sx, sy, sz], palette.wall, {
      ry: (tileRandom(seed, wall.x, wall.y, 8) - 0.5) * k.rockTilt,
    });
  }
  return { timber, iron, rock, shaped, bed: rails.bed, frames, tracks: rails.tracks, obstacles, dressing: dressing.counts };
};
